<template>
  <footer
    v-reveal
    class="text-white"
    :style="{ backgroundColor: 'var(--sideout-navy)', padding: '96px 0 48px' }"
  >
    <div class="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
      <div class="flex flex-col items-center text-center">
        <img
          v-if="foundationLogo"
          :src="foundationLogo"
          :alt="foundationName"
          class="h-10 w-auto object-contain"
        />
        <p
          v-else
          class="sideout-headline"
          :style="{ fontSize: '24px', fontWeight: 800, color: 'white' }"
        >
          {{ foundationName }}
        </p>

        <p
          v-if="footer.tagline"
          class="mt-5 max-w-xl text-[15px] leading-relaxed"
          :style="{ color: 'rgba(255,255,255,0.72)' }"
        >
          {{ footer.tagline }}
        </p>

        <!-- Connect — one centered row of icons (no labels). Each link keeps an
             aria-label/title so it stays accessible without visible text, and
             opens off-site destinations in a new tab. -->
        <ul
          v-if="connectLinks.length > 0"
          class="mt-6 flex flex-wrap items-center justify-center gap-2"
        >
          <li v-for="(link, index) in connectLinks" :key="index">
            <!-- :href is scheme-guarded; v-html renders only the hardcoded
                 ICONS.* constants (never link.* data) — keep it that way. -->
            <a
              v-if="link.link"
              :href="safeHref(link.link)"
              :aria-label="link.label"
              :title="link.label"
              class="v2-footer__social"
              :target="opensInNewTab(link.link) ? '_blank' : null"
              :rel="opensInNewTab(link.link) ? 'noopener noreferrer' : null"
              v-html="iconForLink(link)"
            ></a>
            <span
              v-else
              :aria-label="link.label"
              :title="link.label"
              class="v2-footer__social v2-footer__social--static"
              v-html="iconForLink(link)"
            ></span>
          </li>
        </ul>
      </div>

      <p
        v-if="footer.copyright"
        class="mt-14 pt-8 text-center text-xs"
        :style="{ borderTop: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }"
      >
        {{ footer.copyright }}
      </p>
    </div>
  </footer>
</template>

<script>
// Inline brand glyphs — no icon-library dependency, mirroring V2ShareModal's
// self-contained pattern. Each is a solid 24×24 path filled with currentColor,
// so the anchor's `color` (and its hover state) drives the icon color.
const ICONS = {
  instagram:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>'
    + '</svg>',
  facebook:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>'
    + '</svg>',
  twitter:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>'
    + '</svg>',
  youtube:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>'
    + '</svg>',
  tiktok:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>'
    + '</svg>',
  mail:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>'
    + '</svg>',
  globe:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">'
    + '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>'
    + '</svg>',
}

export default {
  name: 'V2Footer',
  props: {
    foundation: { type: Object, default: null },
    footer: { type: Object, default: () => ({}) },
  },
  computed: {
    foundationName() {
      return this.foundation?.name || 'Foundation'
    },
    foundationLogo() {
      // Prefer the dedicated (white-on-dark) footer logo from template content
      return this.footer?.logo || this.foundation?.logo_path || this.foundation?.logo || null
    },
    connectLinks() {
      return Array.isArray(this.footer?.connect_links) ? this.footer.connect_links : []
    },
  },
  methods: {
    // Map a {label, link} connect entry to a brand glyph. Both the label and
    // the URL are checked (via has()) and either can match; the x.com check is
    // URL-only. Falls back to a mail glyph for contact-ish entries and a globe
    // for anything unrecognized. Data model unchanged — editors still add
    // label/link rows in the admin Footer editor; we render them as icons.
    iconForLink(link) {
      const label = (link?.label || '').toLowerCase()
      const url = (link?.link || '').toLowerCase()
      const has = (term) => label.includes(term) || url.includes(term)

      if (has('instagram')) return ICONS.instagram
      if (has('facebook')) return ICONS.facebook
      if (has('youtube') || url.includes('youtu.be')) return ICONS.youtube
      if (has('tiktok')) return ICONS.tiktok
      if (has('twitter') || url.includes('x.com')) return ICONS.twitter
      if (
        label.includes('contact') ||
        label.includes('mail') ||
        label.includes('email') ||
        label.includes('@') ||
        url.startsWith('mailto:')
      ) {
        return ICONS.mail
      }
      // No brand matched — generic globe. To add a brand, extend ICONS and the
      // has() branches above.
      return ICONS.globe
    },
    // Neutralize script-y schemes from admin-authored, DB-backed link values
    // before binding to :href; returns null (no href) for those.
    safeHref(url) {
      if (typeof url !== 'string') return null
      const trimmed = url.trim()
      return /^(javascript|data|vbscript):/i.test(trimmed) ? null : trimmed
    },
    // Open absolute off-site web URLs (http/https/protocol-relative) in a new
    // tab. Same-site relative paths, in-page anchors, and native-handoff
    // schemes (mailto/tel/sms) stay in the current context.
    opensInNewTab(url) {
      if (typeof url !== 'string') return false
      return /^(https?:)?\/\//i.test(url.trim())
    },
  },
}
</script>

<style scoped>
/* Minimal icon link: bare glyph with a comfortable 40px tap target (the glyph
   itself is 20px). No background or border — matches V2's restrained look —
   with a pink-light hover lift consistent with the footer's text-link hover. */
.v2-footer__social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  color: rgba(255, 255, 255, 0.72);
  transition: color 200ms ease, transform 200ms ease;
}

.v2-footer__social:hover {
  color: var(--sideout-pink-light);
  transform: translateY(-2px);
}

.v2-footer__social:focus-visible {
  outline: 2px solid var(--sideout-pink-light);
  outline-offset: 2px;
}

/* Rare fallback: a connect entry with no link is non-interactive. */
.v2-footer__social--static {
  cursor: default;
}

.v2-footer__social--static:hover {
  color: rgba(255, 255, 255, 0.72);
  transform: none;
}
</style>
